import { asOneOf, useQuery } from "portal/utils/hooks/useQuery";
import { CategorizedImages } from "portal/components/categoryCollectionProfile/categorized/CategorizedImages";
import { Category } from "protos/robot_syncer/plant_profile";
import { CategoryTemplateHistory } from "portal/components/categoryCollectionProfile/history/CategoryTemplateHistory";
import { CategoryTemplateHistoryProvider } from "portal/components/categoryCollectionProfile/history/VersionHistoryProvider";
import { CategoryTemplateInstructionsDialog } from "portal/components/categoryCollectionProfile/instructions/CategoryTemplateInstructionsDialog";
import { CategoryTemplateViewTab } from "portal/views/categoryTemplates/tabs";
import { classes } from "portal/theme/theme";
import { RadioChipOption } from "portal/components/RadioChips";
import { resolveOriginCategory } from "portal/utils/categoryCollection/category";
import { ShootTimeSection } from "portal/components/shootTime/ShootTimeSection";
import { Tab, Tabs } from "@mui/material";
import { TAB_QUERY } from "portal/utils/routing";
import { TabContext, TabPanel } from "@mui/lab";
import { ThumbnailControls } from "portal/components/categoryCollectionProfile/ThumbnailControls";
import { useCarbonDefaultAlmanac } from "portal/components/shootTime/useCarbonDefaultAlmanac";
import { useProtectedThumbnailSize } from "portal/components/images/imageSizing/useProtectedThumbnailSize";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from "portal/state/store";
import React, { FC } from "react";

interface CategoryTemplateViewProps {
  category: Category;
  categoryOptions: RadioChipOption[];
  categorizationMap: Record<string, string>;
  currentVersionId?: string;
  onRevert?: (revertToVersionId: string) => Promise<unknown>;
  isReverting: boolean;
  onRevive?: (
    predictionPointId?: string,
    categoryId?: string
  ) => void | Promise<unknown>;
}

export const CategoryTemplateView: FC<CategoryTemplateViewProps> = ({
  category,
  categoryOptions,
  categorizationMap,
  currentVersionId,
  onRevert,
  isReverting,
  onRevive,
}) => {
  const { t } = useTranslation();
  const { almanac: carbonDefaultAlmanac } = useCarbonDefaultAlmanac();
  const [tabParam, setTab] = useQuery(
    TAB_QUERY,
    asOneOf(
      CategoryTemplateViewTab.CATEGORIZED,
      CategoryTemplateViewTab.HISTORY
    )
  );
  const tab = tabParam ?? CategoryTemplateViewTab.CATEGORIZED;
  const { categoryCollection } = useUserPreferences();
  const {
    thumbnailDivRef,
    measuredThumbnailMaxSize,
    thumbnailSize,
    dimensions,
  } = useProtectedThumbnailSize(categoryCollection.thumbnailSize);

  return (
    <TabContext value={tab}>
      <div className="flex flex-col md:flex-row justify-center md:justify-between items-start md:items-end">
        <div className="flex items-center gap-6">
          <Tabs
            value={tab}
            onChange={(
              _: React.SyntheticEvent,
              newValue: CategoryTemplateViewTab
            ) => {
              setTab(newValue);
            }}
          >
            <Tab
              label={t(
                "components.categoryCollectionProfile.images.categorized"
              )}
              className="text-inherit"
              value={CategoryTemplateViewTab.CATEGORIZED}
            />
            {currentVersionId ? (
              <Tab
                label={t("views.fleet.robots.history.navTitle")}
                className="text-inherit"
                value={CategoryTemplateViewTab.HISTORY}
              />
            ) : undefined}
          </Tabs>
          <CategoryTemplateInstructionsDialog />
        </div>
        <ThumbnailControls
          measuredThumbnailMaxSize={measuredThumbnailMaxSize}
          thumbnailSize={thumbnailSize}
        />
      </div>
      <TabPanel
        value={CategoryTemplateViewTab.CATEGORIZED}
        className={classes("pt-2 pl-0 pr-0", "overflow-visible")}
      >
        <div ref={thumbnailDivRef} className="flex flex-col gap-4">
          <CategorizedImages
            variant="flat"
            categoryId={category.id}
            categoryOptions={categoryOptions}
            predictionPointIds={category.chipIds}
            dimensions={dimensions}
            categorizationMap={categorizationMap}
          />
          <ShootTimeSection
            almanac={category.almanac}
            canEdit={false}
            carbonDefaultAlmanac={carbonDefaultAlmanac}
            originCategory={resolveOriginCategory(
              category.almanac?.originCategoryId
            )}
            subtitle={t("views.categoryTemplates.shootTime.subnote")}
          />
        </div>
      </TabPanel>
      {currentVersionId ? (
        <TabPanel
          value={CategoryTemplateViewTab.HISTORY}
          className={classes("pt-2 pl-0 pr-0", "overflow-visible")}
        >
          <CategoryTemplateHistoryProvider
            categoryId={category.id}
            currentVersionId={currentVersionId}
          >
            <CategoryTemplateHistory
              uuid={category.id}
              dimensions={dimensions}
              onRevert={onRevert}
              isReverting={isReverting}
              currentVersionCategorizationMap={categorizationMap}
              onClassify={onRevive}
            />
          </CategoryTemplateHistoryProvider>
        </TabPanel>
      ) : undefined}
    </TabContext>
  );
};
